import React, { useEffect, useState } from 'react';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import { Sidebar } from '../components/Sidebar';
import { recipesService } from '../services/recipes';
import { runOcr } from '../services/ocr';
import Loader from '../components/Loader';
import { useT } from '../i18n';

export const RecipesPage = () => {
  const { t } = useT();
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadRecipes();
  }, []);

  const loadRecipes = async (category = null) => {
    try {
      setLoading(true);
      const data = await recipesService.getAll(category);
      setRecipes(data);
    } catch (err) {
      setError('Failed to load recipes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reload recipes when category filter changes
  useEffect(() => {
    loadRecipes(categoryFilter);
  }, [categoryFilter]);

  // Filter recipes based on search query (category filtering is done on backend)
  const filteredRecipes = recipes.filter(recipe => 
    recipe.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recipe.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredRecipes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecipes = filteredRecipes.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleDeleteRecipe = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) {
      return;
    }

    try {
      await recipesService.delete(id);
      loadRecipes();
      if (selectedRecipe && selectedRecipe.id === id) {
        setSelectedRecipe(null);
      }
    } catch (err) {
      setError('Failed to delete recipe: ' + err.message);
    }
  };

  const handleRecipeSelect = async (recipe) => {
    try {
      const fullRecipe = await recipesService.getById(recipe.id);
      setSelectedRecipe(fullRecipe);
    } catch (err) {
      setError('Failed to load recipe details: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <ErrorBoundary>
        <Shell sidebar={<Sidebar />}>
          <div className="main-content busy-wrapper">
            <Loader />
          </div>
        </Shell>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Shell sidebar={<Sidebar />}>
        <div className="main-content busy-wrapper">
          {/* Header matching the image design */}
          <div className="page-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '12px',
            borderBottom: '1px solid #e5e7eb',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h1 style={{ 
              fontSize: 'clamp(18px, 4vw, 24px)', 
              fontWeight: '700', 
              color: '#111827',
              margin: 0
            }}>
              {t('recp.title')}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {/* Category Filter Dropdown */}
              <div style={{ position: 'relative', width: '220px', maxWidth: '100%' }}>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '4px 32px 4px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '13px',
                    height: '24px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    background: '#ffffff',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    color: '#111827',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    backgroundSize: '16px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3fffd8';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                  }}
                >
                  <option value="all">{t('recp.allCategories')}</option>
                  <option value="scope1_electricity">{t('recp.catScope1Electricity')}</option>
                  <option value="scope1_mobile">{t('recp.catScope1Mobile')}</option>
                  <option value="scope1_stationary">{t('recp.catScope1Stationary')}</option>
                  <option value="scope1_heating">{t('recp.catScope1Heating')}</option>
                  <option value="scope1_electric_vehicles">{t('recp.catScope1Ev')}</option>
                  <option value="scope2_electricity">{t('recp.catScope2Electricity')}</option>
                  <option value="scope3_business_travel">{t('recp.catScope3Travel')}</option>
                </select>
              </div>

              {/* Modern Search Input */}
              <div style={{ position: 'relative', width: '280px', maxWidth: '100%' }}>
                <svg 
                  style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '14px',
                    height: '14px',
                    pointerEvents: 'none',
                    color: '#9ca3af'
                  }}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input 
                  type="text" 
                  placeholder="Search recipes..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                  padding: '4px 8px 4px 32px',
                  border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                  fontSize: '13px',
                  height: '24px',
                  minWidth: '40px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                    background: '#ffffff',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    color: '#111827'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3fffd8';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                  }}
                />
              </div>

              {/* <button 
                className="btn-primary"
                onClick={() => setShowUpload(!showUpload)}
                style={{
                  background: showUpload ? 'linear-gradient(180deg, #6b7280, #4b5563)' : 'linear-gradient(180deg,#3fffd8,#14e0bb)',
                  color: showUpload ? '#ffffff' : '#0b1f1b',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '4px 12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  height: '24px',
                  minWidth: '40px',
                  whiteSpace: 'nowrap',
                  boxShadow: showUpload ? '0 1px 3px rgba(0,0,0,0.1)' : '0 4px 12px rgba(20,224,187,0.35)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = showUpload ? '0 2px 6px rgba(0,0,0,0.15)' : '0 6px 16px rgba(20,224,187,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = showUpload ? '0 1px 3px rgba(0,0,0,0.1)' : '0 4px 12px rgba(20,224,187,0.35)';
                }}
              >
                <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {showUpload ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  )}
                </svg>
                {showUpload ? 'Cancel' : 'Upload Recipe'}
              </button> */}
            </div>
          </div>

          {error && (
            <div className="error-message" style={{ 
              background: '#fee2e2', 
              color: '#dc2626', 
              padding: '12px', 
              borderRadius: '6px', 
              margin: '16px 0',
              border: '1px solid #fecaca'
            }}>
              {error}
              <button onClick={() => setError(null)} style={{ 
                float: 'right', 
                background: 'none', 
                border: 'none', 
                color: '#dc2626', 
                cursor: 'pointer',
                fontSize: '18px'
              }}>×</button>
            </div>
          )}

          {/* Table matching the image design */}
          <div className="table-container" style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <div className="table-header" style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: '700', 
                color: '#111827' 
              }}>
                {t('recp.listTitle')}
              </h2>
            </div>

            <div className="table-content" style={{ padding: '0' }}>
              {loading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#6b7280',
                  fontSize: '16px'
                }}>
                  Loading recipes...
                </div>
              ) : filteredRecipes.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#6b7280',
                  fontSize: '16px'
                }}>
                  {searchQuery ? `No recipes found matching "${searchQuery}"` : 'No recipes found. Upload invoices in Scope 1 pages to see them here!'}
                </div>
              ) : (
                <>
                <div>
                    {/* Table Header - Desktop only */}
                  <div className="table-header-row" style={{
                      display: 'none',
                    gridTemplateColumns: '40px 1fr 200px 100px 100px',
                    gap: '16px',
                    padding: '16px 24px',
                    background: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '14px'
                  }}>
                    <div>{t('recp.colSelect')}</div>
                    <div>{t('recp.colName')}</div>
                    <div>{t('recp.colDate')}</div>
                    <div>{t('recp.colView')}</div>
                    <div>{t('recp.colDelete')}</div>
                  </div>

                  {/* Table Rows */}
                  {paginatedRecipes.map(recipe => (
                    <div 
                      key={recipe.id}
                      className="table-row" 
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '40px 1fr 200px 100px 100px',
                        gap: '16px',
                        padding: '16px 24px',
                        borderBottom: '1px solid #f3f4f6',
                        alignItems: 'center',
                        transition: 'background-color 0.2s',
                          cursor: 'pointer',
                          position: 'relative'
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <div>
                        <input 
                          type="checkbox" 
                            style={{ width: '20px', height: '20px', minWidth: '20px', minHeight: '20px' }}
                        />
                      </div>
                      <div style={{ 
                        fontWeight: '500', 
                        color: '#111827',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span>{recipe.title?.replace(/[^\x20-\x7E]/g, '') || 'Untitled Recipe'}</span>
                        {recipe.source === 'scope1' && (
                          <span style={{
                            background: 'linear-gradient(135deg, #3fffd8, #2dd4bf)',
                            color: '#1a1a1a',
                            fontSize: '10px',
                            fontWeight: '600',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                          }}>
                            Scope 1
                          </span>
                        )}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '14px' }}>
                        {formatDate(recipe.created_at)}
                      </div>
                      <div>
                        <button
                          onClick={() => handleRecipeSelect(recipe)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#3b82f6',
                            cursor: 'pointer',
                            padding: '4px',
                              borderRadius: '4px',
                              height: '28px',
                              minWidth: '28px',
                              fontSize: '16px'
                          }}
                          title="View Recipe"
                            aria-label="View Recipe"
                        >
                          👁️
                        </button>
                      </div>
                      <div>
                        <button
                          onClick={() => handleDeleteRecipe(recipe.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            padding: '4px',
                              borderRadius: '4px',
                              height: '28px',
                              minWidth: '28px',
                              fontSize: '16px'
                          }}
                          title="Delete Recipe"
                            aria-label="Delete Recipe"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                  
                  <style>{`
                    @media (min-width: 769px) {
                      .table-header-row {
                        display: grid !important;
                      }
                    }
                    @media (max-width: 768px) {
                      .table-row {
                        grid-template-columns: 1fr !important;
                        gap: 8px !important;
                        padding: 16px !important;
                      }
                      .table-row > div:first-child {
                        position: absolute;
                        right: 16px;
                        top: 16px;
                      }
                      .table-row > div:nth-child(2) {
                        grid-column: 1;
                        font-size: 16px;
                        padding-right: 50px;
                      }
                      .table-row > div:nth-child(3) {
                        font-size: 12px;
                      }
                      .table-row > div:nth-child(4),
                      .table-row > div:nth-child(5) {
                        display: inline-block;
                      }
                    }
                  `}</style>
                </>
              )}
            </div>
          </div>

          {/* Pagination Controls */}
          {filteredRecipes.length > 0 && totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              marginTop: '24px',
              marginBottom: '24px',
              flexWrap: 'wrap',
              padding: '0 16px'
            }}>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '10px 16px',
                  border: '1px solid #e5e7eb',
                  background: currentPage === 1 ? '#f3f4f6' : 'white',
                  color: currentPage === 1 ? '#9ca3af' : '#374151',
                  borderRadius: '8px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '40px',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => currentPage !== 1 && (e.target.style.background = '#f9fafb')}
                onMouseLeave={(e) => currentPage !== 1 && (e.target.style.background = 'white')}
              >
                ← Previous
              </button>

              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first page, last page, current page, and pages around current
                    return page === 1 || 
                           page === totalPages || 
                           Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span style={{ padding: '0 4px', color: '#9ca3af' }}>...</span>
                      )}
                      <button
                        onClick={() => goToPage(page)}
                        style={{
                          padding: '10px 16px',
                          border: '1px solid #e5e7eb',
                          background: currentPage === page ? '#3fffd8' : 'white',
                          color: currentPage === page ? 'white' : '#374151',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: currentPage === page ? '600' : '500',
                          minWidth: '44px',
                          minHeight: '40px',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => currentPage !== page && (e.target.style.background = '#f9fafb')}
                        onMouseLeave={(e) => currentPage !== page && (e.target.style.background = 'white')}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '10px 16px',
                  border: '1px solid #e5e7eb',
                  background: currentPage === totalPages ? '#f3f4f6' : 'white',
                  color: currentPage === totalPages ? '#9ca3af' : '#374151',
                  borderRadius: '8px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '40px',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => currentPage !== totalPages && (e.target.style.background = '#f9fafb')}
                onMouseLeave={(e) => currentPage !== totalPages && (e.target.style.background = 'white')}
              >
                Next →
              </button>

              <span style={{ 
                marginLeft: '12px', 
                fontSize: '14px', 
                color: '#6b7280',
                whiteSpace: 'nowrap'
              }}>
                Page {currentPage} of {totalPages} ({filteredRecipes.length} total)
              </span>
            </div>
          )}

          {/* Recipe Content Modal/Detail View */}
          {selectedRecipe && (
            <div className="recipe-detail-modal" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}>
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '0',
                maxWidth: '700px',
                width: '100%',
                maxHeight: '80vh',
                overflow: 'hidden',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column'
              }}>
              <style>{`
                @media (max-width: 768px) {
                  .recipe-detail-modal > div {
                    max-width: 100% !important;
                    width: 100% !important;
                    height: 100% !important;
                    max-height: 100vh !important;
                    border-radius: 0 !important;
                    margin: 0 !important;
                  }
                  .recipe-detail-modal {
                    padding: 0 !important;
                  }
                }
              `}</style>
                {/* Header */}
                <div style={{
                  padding: '20px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <h2 style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#111827',
                      marginBottom: '4px'
                    }}>
                      {selectedRecipe.title?.replace(/[^\x20-\x7E]/g, '') || 'Untitled Recipe'}
                    </h2>
                    <div style={{
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      {selectedRecipe.file_name?.replace(/[^\x20-\x7E]/g, '') || 'Unknown file'}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedRecipe(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '4px'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Content */}
                <div style={{
                  padding: '20px',
                  overflow: 'auto',
                  flex: 1
                }}>
                  {/* Parse and display structured data */}
                  {(() => {
                    const content = selectedRecipe.content || '';
                    
                    // Try to parse table data
                    const tableRows = [];
                    const consumptionMatch = content.match(/Monthly consumption[^|]*\|[^|]*\|[^|]*\|[^|]*/);
                    
                    if (consumptionMatch) {
                      const tableData = content.match(/(A[12IL]\/B[12ILZ])\s*\|\s*(\d+)\s*\|\s*([\d.]+)\s*\|\s*([\d.]+)/g);
                      if (tableData) {
                        tableRows.push(...tableData.map(row => {
                          const parts = row.split('|').map(p => p.trim());
                          return {
                            line: parts[0],
                            consumption: parts[1],
                            price: parts[2],
                            amount: parts[3]
                          };
                        }));
                      }
                    }

                    return (
                      <div>
                        {tableRows.length > 0 && (
                          <div style={{
                            background: '#f9fafb',
                            borderRadius: '6px',
                            padding: '10px',
                            marginBottom: '16px',
                            border: '1px solid #e5e7eb'
                          }}>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                              Consumption Data
                            </h3>
                            <div style={{ display: 'grid', gap: '8px', overflowX: 'auto' }}>
                              {tableRows.map((row, index) => (
                                <div key={index} style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 100px 100px 100px',
                                  gap: '12px',
                                  padding: '6px 8px',
                                  background: 'white',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  border: '1px solid #e5e7eb',
                                  minWidth: '400px'
                                }}>
                                  <div style={{ fontWeight: '500', color: '#111827' }}>{row.line}</div>
                                  <div style={{ textAlign: 'right', color: '#6b7280' }}>{row.consumption} kWh</div>
                                  <div style={{ textAlign: 'right', color: '#6b7280' }}>{row.price}</div>
                                  <div style={{ textAlign: 'right', color: '#6b7280' }}>{row.amount}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </Shell>
    </ErrorBoundary>
  );
};
